import AtradiusClientRow, {
  coverStatus,
} from "../components/AtradiusClientRow";
import PageHeading from "../components/PageHeading";
import { createListSearch } from "../utils/listSearch";
import { useEffect, useState } from "react";
import { Search, Loader2 } from "lucide-react";
import axios from "../axios/AxiosInstance";
import ClientDetail from "./ClientDetail";

export default function AtradiusPage() {
  const [clients, setClients] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    async function load() {
      try {
        const { data } = await axios.get("client/freelance");
        const list = Array.isArray(data) ? data : [];
        if (active) setClients(list);
        const results = [];
        let cursor = 0;
        let failed = 0;
        await Promise.all(
          Array.from({ length: Math.min(6, list.length) }, async () => {
            while (active && cursor < list.length) {
              const client = list[cursor++];
              try {
                const response = await axios.get(`cover/client/${client._id}`);
                const cover = Array.isArray(response.data)
                  ? response.data[0]
                  : response.data;
                if (cover) results.push({ client, cover });
              } catch (e) {
                if (e.response?.status !== 404) failed++;
              }
            }
          }),
        );
        if (active) {
          setRows(results);
          if (failed)
            setError(
              `${failed} couverture(s) n’ont pas pu être chargées. Vous pouvez réessayer.`,
            );
        }
      } catch {
        if (active) {
          setRows([]);
          setError("Impossible de charger les couvertures Atradius.");
        }
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [reload]);
  const filtered = rows
    .filter(
      createListSearch(search, [
        "client.companyName",
        "client.denomination",
        "client.siret",
        "client.town",
        "client.contactPhone",
        "client.contactEMail",
        "cover.coverType",
        "cover._doc.coverType",
        "cover.status",
        "cover.requestDate",
        "cover._doc.requestDate",
      ]),
    )
    .filter(
      (row) =>
        statusFilter === "all" || coverStatus(row.cover) === statusFilter,
    );
  return (
    <div className="">
      <header className="mb-6 flex items-center gap-3">
        <div>
          <PageHeading
            icon="atradius"
            title={<> Atradius </>}
            description={
              <>
                {" "}
                Suivez les demandes de couverture et les montants accordés à vos
                clients.{" "}
              </>
            }
          />
        </div>
      </header>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-md">
          <Search
            size={18}
            aria-hidden="true"
            className="absolute left-3 top-3 text-slate-400"
          />
          <input
            aria-label="Rechercher une couverture par client ou SIRET"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un client ou un SIRET…"
            className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          aria-label="Filtrer par état Atradius"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
        >
          <option value="all">Tous les états</option>
          {["OK", "En attente", "Refusée", "À revoir"].map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
        {statusFilter !== "all" && (
          <button
            type="button"
            onClick={() => setStatusFilter("all")}
            className="text-sm text-blue-700 hover:underline"
          >
            Réinitialiser
          </button>
        )}
        <button
          disabled={loading}
          onClick={() => setReload((value) => value + 1)}
          className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 hover:bg-blue-50 disabled:opacity-50"
        >
          Actualiser
        </button>
      </div>
      {error && (
        <p
          role="alert"
          className="mb-4 rounded-xl bg-amber-50 p-4 text-sm text-amber-800"
        >
          {error}
        </p>
      )}
      {loading ? (
        <div
          role="status"
          className="flex justify-center gap-3 py-16 text-slate-500"
        >
          <Loader2 className="animate-spin" />
          Chargement des couvertures…
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                {[
                  "Client",
                  "Date demande",
                  "Type",
                  "Demandé",
                  "Accordé",
                  "Facture",
                  "État",
                  "Action",
                ].map((label) => (
                  <th
                    key={label}
                    scope="col"
                    className="whitespace-nowrap px-4 py-4 font-semibold"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(({ client, cover }) => (
                <AtradiusClientRow
                  key={client._id}
                  client={client}
                  cover={cover}
                  onOpen={setSelected}
                />
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-12 text-center text-slate-500"
                  >
                    {search || statusFilter !== "all"
                      ? "Aucune couverture ne correspond à vos filtres."
                      : "Aucune couverture Atradius disponible."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {selected && (
        <ClientDetail
          client={selected}
          setClient={setSelected}
          clients={clients}
          setClients={setClients}
          originalClients={clients}
          initialSection="financial"
        />
      )}
    </div>
  );
}
